// Descriptori de burse pentru **browser**.
//
// Fetch-ul de lumânări și stream-urile live merg direct de la browser la bursă —
// serverul nu e în cale. Fiecare adaptor expune aceeași formă:
//
//   restKlines(market, native, tf, limit) -> Promise<Bar[]>
//   wsUrl(market)                         -> string
//   subscribe(kind, market, native, tf)   -> { topic, message }
//   unsubscribe(topic, ...)               -> message
//   parse(raw)                            -> { topic, kind, payload } | null
//
// Bar = { time: <secunde>, open, high, low, close, volume }  (volume = în cotare, USDT)

import { splitSymbol, TF_SECONDS } from './util.js';

/** Rutează prin proxy-ul nostru dacă bursa e blocată în rețeaua utilizatorului. */
export const proxy = {
  enabled: localStorage.getItem('sx_use_proxy') === '1',
  target(host) {
    const map = {
      'https://fapi.binance.com': 'binance-futures',
      'https://api.binance.com': 'binance-spot',
      'https://api.bybit.com': 'bybit',
      'https://www.okx.com': 'okx',
      'https://api.bitget.com': 'bitget',
      'https://api.gateio.ws': 'gate',
      'https://api.mexc.com': 'mexc-spot',
      'https://contract.mexc.com': 'mexc-futures',
    };
    return map[host] || null;
  },
  url(host, path, params) {
    const qs = new URLSearchParams(params).toString();
    if (this.enabled) {
      const target = this.target(host);
      if (target) return `/api/proxy/${target}${path}${qs ? `?${qs}` : ''}`;
    }
    return `${host}${path}${qs ? `?${qs}` : ''}`;
  },
};

/**
 * Gazde care nu răspund direct din browser — de obicei din lipsă de CORS.
 * `api.binance.com` e un caz real: spotul nu trimite `Access-Control-Allow-Origin`,
 * deși futures-ul o face. Le ținem minte ca să nu mai irosim o cerere eșuată
 * înainte de fiecare ocolire prin serverul nostru.
 */
const blockedHosts = new Set();

function proxiedUrl(host, path, params) {
  const target = proxy.target(host);
  const qs = new URLSearchParams(params || {}).toString();
  if (!target) return null;
  return `/api/proxy/${target}${path}${qs ? `?${qs}` : ''}`;
}

/**
 * Datele de piață nu se cachează niciodată.
 *
 * Interogarea de rezervă cere aceeași adresă la fiecare câteva secunde. Fără asta
 * browserul servea răspunsul din cache-ul HTTP și graficele rămâneau înghețate pe
 * lumânarea de la prima cerere, deși în panoul de rețea totul arăta 200 OK.
 */
const NO_CACHE = { cache: 'no-store' };

async function getJson(host, path, params) {
  if (blockedHosts.has(host)) {
    const url = proxiedUrl(host, path, params);
    if (url) {
      const resp = await fetch(url, NO_CACHE);
      if (!resp.ok) throw new Error(`${resp.status} ${host}${path}`);
      return resp.json();
    }
  }

  let resp;
  try {
    resp = await fetch(proxy.url(host, path, params), NO_CACHE);
  } catch (err) {
    // Eșec de rețea sau CORS: gazda nu e accesibilă direct, trecem prin server.
    const url = proxiedUrl(host, path, params);
    if (!url) throw err;
    blockedHosts.add(host);
    console.warn(`[exchanges] ${host} nu răspunde direct — rutez prin proxy`);
    const viaProxy = await fetch(url, NO_CACHE);
    if (!viaProxy.ok) throw new Error(`${viaProxy.status} ${host}${path}`);
    return viaProxy.json();
  }
  // Un 4xx e răspunsul bursei (simbol inexistent), nu o problemă de acces.
  if (!resp.ok) throw new Error(`${resp.status} ${host}${path}`);
  return resp.json();
}

const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));

// ---------------------------------------------------------------- BINANCE

const BINANCE_HOST = { futures: 'https://fapi.binance.com', spot: 'https://api.binance.com' };
const BINANCE_PATH = { futures: '/fapi/v1/klines', spot: '/api/v3/klines' };
// Futures Binance și-a împărțit stream-urile pe două adrese. Măsurat pe 2026-10-04:
// pe vechiul `/stream` lumânările, `aggTrade` și tickerele nu mai livrează nimic (doar
// stakanul mai merge), deci graficele trăiau numai din bătaia REST. Acum lumânările,
// tranzacțiile și tickerele stau pe `/market`, iar stakanul pe `/public` — `/market`
// nu-l livrează.
const BINANCE_WS = {
  futures: 'wss://fstream.binance.com/market/stream',
  spot: 'wss://stream.binance.com:9443/stream',
};
const BINANCE_WS_DEPTH = { futures: 'wss://fstream.binance.com/public/stream' };

const binance = {
  key: 'binance',
  label: 'Binance',
  tf: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1h', '4h': '4h', '1d': '1d' },
  native: (symbol) => symbol,

  async restKlines(market, native, tf, limit, endTime = null) {
    const params = { symbol: native, interval: this.tf[tf], limit };
    if (endTime) params.endTime = endTime;
    const rows = await getJson(BINANCE_HOST[market], BINANCE_PATH[market], params);
    return rows.map((r) => ({
      time: Math.floor(r[0] / 1000),
      open: num(r[1]), high: num(r[2]), low: num(r[3]), close: num(r[4]),
      volume: num(r[7]),
    }));
  },

  wsUrl: (market, route = '') => (route === 'depth' && BINANCE_WS_DEPTH[market]) || BINANCE_WS[market],
  /** Pe ce socket merge un fel de date; '' e socketul principal. */
  wsRoute: (kind, market) => (kind === 'depth' && BINANCE_WS_DEPTH[market] ? 'depth' : ''),

  topic(kind, native, tf) {
    const s = native.toLowerCase();
    if (kind === 'kline') return `${s}@kline_${this.tf[tf]}`;
    if (kind === 'trade') return `${s}@aggTrade`;
    if (kind === 'depth') return `${s}@depth20@100ms`;
    if (kind === 'tickerAll') return '!ticker@arr';
    if (kind === 'ticker') return `${s}@ticker`;
    return null;
  },

  subMessage: (topics, id) => ({ method: 'SUBSCRIBE', params: topics, id }),
  unsubMessage: (topics, id) => ({ method: 'UNSUBSCRIBE', params: topics, id }),

  parse(raw) {
    const stream = raw.stream;
    const d = raw.data;
    if (!stream || !d) return null;
    if (stream.includes('@kline')) {
      const k = d.k;
      return {
        topic: stream, kind: 'kline',
        payload: {
          bar: {
            time: Math.floor(k.t / 1000),
            open: num(k.o), high: num(k.h), low: num(k.l), close: num(k.c),
            volume: num(k.q),
          },
          closed: k.x,
        },
      };
    }
    if (stream.includes('@aggTrade')) {
      return {
        topic: stream, kind: 'trade',
        payload: { time: Math.floor(d.T / 1000), price: num(d.p), qty: num(d.q), buyerMaker: d.m },
      };
    }
    if (stream.includes('@depth')) {
      return {
        topic: stream, kind: 'depth',
        payload: {
          bids: (d.b || d.bids || []).map((x) => [num(x[0]), num(x[1])]),
          asks: (d.a || d.asks || []).map((x) => [num(x[0]), num(x[1])]),
        },
      };
    }
    if (stream === '!ticker@arr') {
      return {
        topic: stream, kind: 'tickerAll',
        payload: d.map((t) => ({
          symbol: t.s, price: num(t.c), change24h: num(t.P),
          high24h: num(t.h), low24h: num(t.l), quoteVolume: num(t.q), trades: num(t.n),
        })),
      };
    }
    if (stream.includes('@ticker')) {
      return {
        topic: stream, kind: 'ticker',
        payload: { symbol: d.s, price: num(d.c), change24h: num(d.P), quoteVolume: num(d.q) },
      };
    }
    return null;
  },
};

// ------------------------------------------------------------------ BYBIT

const BYBIT_HOST = 'https://api.bybit.com';
const BYBIT_CATEGORY = { futures: 'linear', spot: 'spot' };

const bybit = {
  key: 'bybit',
  label: 'Bybit',
  tf: { '1m': '1', '5m': '5', '15m': '15', '1h': '60', '4h': '240', '1d': 'D' },
  native: (symbol) => symbol,

  async restKlines(market, native, tf, limit, endTime = null) {
    const params = {
      category: BYBIT_CATEGORY[market], symbol: native, interval: this.tf[tf], limit,
    };
    if (endTime) params.end = endTime;
    const data = await getJson(BYBIT_HOST, '/v5/market/kline', params);
    const rows = data?.result?.list || [];
    return rows.map((r) => ({
      time: Math.floor(Number(r[0]) / 1000),
      open: num(r[1]), high: num(r[2]), low: num(r[3]), close: num(r[4]),
      volume: num(r[6]),
    })).reverse();
  },

  wsUrl: (market) => `wss://stream.bybit.com/v5/public/${BYBIT_CATEGORY[market]}`,

  topic(kind, native, tf) {
    if (kind === 'kline') return `kline.${this.tf[tf]}.${native}`;
    if (kind === 'trade') return `publicTrade.${native}`;
    if (kind === 'depth') return `orderbook.50.${native}`;
    if (kind === 'ticker') return `tickers.${native}`;
    return null;   // Bybit nu are stream „toate tickerele" — folosim polling REST.
  },

  subMessage: (topics) => ({ op: 'subscribe', args: topics }),
  unsubMessage: (topics) => ({ op: 'unsubscribe', args: topics }),

  parse(raw) {
    const topic = raw.topic;
    if (!topic || !raw.data) return null;
    if (topic.startsWith('kline.')) {
      const k = raw.data[0];
      return {
        topic, kind: 'kline',
        payload: {
          bar: {
            time: Math.floor(k.start / 1000),
            open: num(k.open), high: num(k.high), low: num(k.low), close: num(k.close),
            volume: num(k.turnover),
          },
          closed: k.confirm,
        },
      };
    }
    if (topic.startsWith('publicTrade.')) {
      const t = raw.data[0];
      return {
        topic, kind: 'trade',
        payload: { time: Math.floor(t.T / 1000), price: num(t.p), qty: num(t.v), buyerMaker: t.S === 'Sell' },
      };
    }
    if (topic.startsWith('orderbook.')) {
      return {
        topic, kind: 'depth',
        payload: {
          bids: (raw.data.b || []).map((x) => [num(x[0]), num(x[1])]),
          asks: (raw.data.a || []).map((x) => [num(x[0]), num(x[1])]),
          snapshot: raw.type === 'snapshot',
        },
      };
    }
    if (topic.startsWith('tickers.')) {
      const t = raw.data;
      return {
        topic, kind: 'ticker',
        payload: {
          symbol: t.symbol, price: num(t.lastPrice),
          change24h: num(t.price24hPcnt) * 100, quoteVolume: num(t.turnover24h),
        },
      };
    }
    return null;
  },
};

// -------------------------------------------------------------------- OKX

const OKX_HOST = 'https://www.okx.com';

const okx = {
  key: 'okx',
  label: 'OKX',
  tf: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' },
  native(symbol, market) {
    const { base, quote } = splitSymbol(symbol);
    return market === 'futures' ? `${base}-${quote}-SWAP` : `${base}-${quote}`;
  },

  async restKlines(market, native, tf, limit, endTime = null) {
    const params = { instId: native, bar: this.tf[tf], limit: Math.min(limit, 300) };
    // La OKX `after` înseamnă „mai vechi decât acest timestamp".
    if (endTime) params.after = endTime;
    const data = await getJson(OKX_HOST, '/api/v5/market/candles', params);
    return (data?.data || []).map((r) => ({
      time: Math.floor(Number(r[0]) / 1000),
      open: num(r[1]), high: num(r[2]), low: num(r[3]), close: num(r[4]),
      volume: num(r[7] ?? r[6]),
    })).reverse();
  },

  // Lumânările sunt pe endpoint-ul „business", restul pe „public".
  wsUrl: () => 'wss://ws.okx.com:8443/ws/v5/business',

  topic(kind, native, tf) {
    if (kind === 'kline') return `candle${this.tf[tf]}|${native}`;
    if (kind === 'trade') return `trades|${native}`;
    if (kind === 'depth') return `books5|${native}`;
    if (kind === 'ticker') return `tickers|${native}`;
    return null;
  },

  subMessage: (topics) => ({
    op: 'subscribe',
    args: topics.map((t) => { const [channel, instId] = t.split('|'); return { channel, instId }; }),
  }),
  unsubMessage: (topics) => ({
    op: 'unsubscribe',
    args: topics.map((t) => { const [channel, instId] = t.split('|'); return { channel, instId }; }),
  }),

  parse(raw) {
    const arg = raw.arg;
    if (!arg || !raw.data) return null;
    const topic = `${arg.channel}|${arg.instId}`;
    if (arg.channel.startsWith('candle')) {
      const k = raw.data[0];
      return {
        topic, kind: 'kline',
        payload: {
          bar: {
            time: Math.floor(Number(k[0]) / 1000),
            open: num(k[1]), high: num(k[2]), low: num(k[3]), close: num(k[4]),
            volume: num(k[7] ?? k[6]),
          },
          closed: k[8] === '1',
        },
      };
    }
    if (arg.channel === 'trades') {
      const t = raw.data[0];
      return {
        topic, kind: 'trade',
        payload: { time: Math.floor(Number(t.ts) / 1000), price: num(t.px), qty: num(t.sz), buyerMaker: t.side === 'sell' },
      };
    }
    if (arg.channel.startsWith('books')) {
      const b = raw.data[0];
      return {
        topic, kind: 'depth',
        payload: {
          bids: (b.bids || []).map((x) => [num(x[0]), num(x[1])]),
          asks: (b.asks || []).map((x) => [num(x[0]), num(x[1])]),
        },
      };
    }
    if (arg.channel === 'tickers') {
      const t = raw.data[0];
      const open24 = num(t.open24h) || num(t.last);
      return {
        topic, kind: 'ticker',
        payload: {
          symbol: t.instId, price: num(t.last),
          change24h: open24 ? ((num(t.last) - open24) / open24) * 100 : 0,
          quoteVolume: num(t.volCcy24h),
        },
      };
    }
    return null;
  },
};

// ----------------------------------------------------------------- BITGET

const BITGET_HOST = 'https://api.bitget.com';
const BITGET_INST = { futures: 'USDT-FUTURES', spot: 'SPOT' };

const bitget = {
  key: 'bitget',
  label: 'Bitget',
  tf: {
    futures: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' },
    spot: { '1m': '1min', '5m': '5min', '15m': '15min', '1h': '1h', '4h': '4h', '1d': '1day' },
  },
  wsTf: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' },
  native: (symbol) => symbol,

  async restKlines(market, native, tf, limit, endTime = null) {
    const path = market === 'futures'
      ? '/api/v2/mix/market/candles' : '/api/v2/spot/market/candles';
    const params = { symbol: native, granularity: this.tf[market][tf], limit };
    if (market === 'futures') params.productType = 'USDT-FUTURES';
    if (endTime) params.endTime = endTime;
    const data = await getJson(BITGET_HOST, path, params);
    return (data?.data || []).map((r) => ({
      time: Math.floor(Number(r[0]) / 1000),
      open: num(r[1]), high: num(r[2]), low: num(r[3]), close: num(r[4]),
      volume: num(r[6]),
    }));
  },

  wsUrl: () => 'wss://ws.bitget.com/v2/ws/public',

  topic(kind, native, tf, market) {
    const inst = BITGET_INST[market];
    if (kind === 'kline') return `${inst}|candle${this.wsTf[tf]}|${native}`;
    if (kind === 'trade') return `${inst}|trade|${native}`;
    if (kind === 'depth') return `${inst}|books15|${native}`;
    if (kind === 'ticker') return `${inst}|ticker|${native}`;
    return null;
  },

  _args: (topics) => topics.map((t) => {
    const [instType, channel, instId] = t.split('|');
    return { instType, channel, instId };
  }),
  subMessage(topics) { return { op: 'subscribe', args: this._args(topics) }; },
  unsubMessage(topics) { return { op: 'unsubscribe', args: this._args(topics) }; },

  parse(raw) {
    const arg = raw.arg;
    if (!arg || !raw.data) return null;
    const topic = `${arg.instType}|${arg.channel}|${arg.instId}`;
    if (arg.channel.startsWith('candle')) {
      const k = raw.data[0];
      return {
        topic, kind: 'kline',
        payload: {
          bar: {
            time: Math.floor(Number(k[0]) / 1000),
            open: num(k[1]), high: num(k[2]), low: num(k[3]), close: num(k[4]),
            volume: num(k[6] ?? k[5]),
          },
          closed: false,
        },
      };
    }
    if (arg.channel === 'trade') {
      const t = raw.data[0];
      return {
        topic, kind: 'trade',
        payload: { time: Math.floor(Number(t.ts) / 1000), price: num(t.price), qty: num(t.size), buyerMaker: t.side === 'sell' },
      };
    }
    if (arg.channel.startsWith('books')) {
      const b = raw.data[0];
      return {
        topic, kind: 'depth',
        payload: {
          bids: (b.bids || []).map((x) => [num(x[0]), num(x[1])]),
          asks: (b.asks || []).map((x) => [num(x[0]), num(x[1])]),
        },
      };
    }
    if (arg.channel === 'ticker') {
      const t = raw.data[0];
      return {
        topic, kind: 'ticker',
        payload: {
          symbol: t.instId, price: num(t.lastPr),
          change24h: num(t.change24h) * 100, quoteVolume: num(t.quoteVolume),
        },
      };
    }
    return null;
  },
};

// ------------------------------------------------------------------- GATE

const GATE_HOST = 'https://api.gateio.ws';

const gate = {
  key: 'gate',
  label: 'Gate',
  tf: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1h', '4h': '4h', '1d': '1d' },
  native(symbol) {
    const { base, quote } = splitSymbol(symbol);
    return `${base}_${quote}`;
  },

  async restKlines(market, native, tf, limit, endTime = null) {
    // Gate lucrează în secunde, nu milisecunde.
    const to = endTime ? Math.floor(endTime / 1000) : null;
    if (market === 'futures') {
      const params = { contract: native, interval: this.tf[tf], limit };
      if (to) params.to = to;
      const rows = await getJson(GATE_HOST, '/api/v4/futures/usdt/candlesticks', params);
      return rows.map((r) => ({
        time: Number(r.t), open: num(r.o), high: num(r.h), low: num(r.l), close: num(r.c),
        volume: num(r.sum),
      }));
    }
    const params = { currency_pair: native, interval: this.tf[tf], limit };
    if (to) params.to = to;
    const rows = await getJson(GATE_HOST, '/api/v4/spot/candlesticks', params);
    // spot: [ts, quoteVol, close, high, low, open, baseVol, closed]
    return rows.map((r) => ({
      time: Number(r[0]), open: num(r[5]), high: num(r[3]), low: num(r[4]), close: num(r[2]),
      volume: num(r[1]),
    })).sort((a, b) => a.time - b.time);
  },

  wsUrl: (market) => (market === 'futures'
    ? 'wss://fx-ws.gateio.ws/v4/ws/usdt' : 'wss://api.gateio.ws/ws/v4/'),

  topic(kind, native, tf, market) {
    const ns = market === 'futures' ? 'futures' : 'spot';
    if (kind === 'kline') return `${ns}.candlesticks|${this.tf[tf]}_${native}`;
    if (kind === 'trade') return `${ns}.trades|${native}`;
    if (kind === 'depth') return `${ns}.order_book|${native}`;
    if (kind === 'ticker') return `${ns}.tickers|${native}`;
    return null;
  },

  _msg(topics, event) {
    // Gate acceptă un singur canal per mesaj — clientul le trimite pe rând.
    const [channel, payload] = topics[0].split('|');
    const parts = payload.split('_');
    let args;
    if (channel.endsWith('candlesticks')) args = [parts[0], parts.slice(1).join('_')];
    else if (channel.endsWith('order_book')) args = [payload, '20', '100ms'];
    else args = [payload];
    return { time: Math.floor(Date.now() / 1000), channel, event, payload: args };
  },
  subMessage(topics) { return this._msg(topics, 'subscribe'); },
  unsubMessage(topics) { return this._msg(topics, 'unsubscribe'); },
  oneAtATime: true,

  parse(raw) {
    const channel = raw.channel;
    const result = raw.result;
    if (!channel || !result || raw.event !== 'update') return null;
    if (channel.endsWith('candlesticks')) {
      const k = Array.isArray(result) ? result[0] : result;
      const [tf, ...rest] = String(k.n || '').split('_');
      return {
        topic: `${channel}|${tf}_${rest.join('_')}`, kind: 'kline',
        payload: {
          bar: {
            time: Number(k.t), open: num(k.o), high: num(k.h), low: num(k.l), close: num(k.c),
            volume: num(k.a ?? k.v),
          },
          closed: false,
        },
      };
    }
    if (channel.endsWith('trades')) {
      const t = Array.isArray(result) ? result[0] : result;
      const pair = t.currency_pair || t.contract;
      return {
        topic: `${channel}|${pair}`, kind: 'trade',
        payload: {
          time: Math.floor(Number(t.create_time_ms ?? t.create_time * 1000) / 1000),
          price: num(t.price), qty: Math.abs(num(t.amount ?? t.size)),
          buyerMaker: num(t.amount ?? t.size) < 0 || t.side === 'sell',
        },
      };
    }
    if (channel.endsWith('order_book')) {
      const pair = result.s || result.contract || result.currency_pair;
      const level = (x) => [num(x.p ?? x[0]), num(x.s ?? x[1])];
      return {
        topic: `${channel}|${pair}`, kind: 'depth',
        payload: {
          bids: (result.bids || []).map(level),
          asks: (result.asks || []).map(level),
        },
      };
    }
    if (channel.endsWith('tickers')) {
      const t = Array.isArray(result) ? result[0] : result;
      const pair = t.currency_pair || t.contract;
      return {
        topic: `${channel}|${pair}`, kind: 'ticker',
        payload: {
          symbol: pair, price: num(t.last),
          change24h: num(t.change_percentage),
          quoteVolume: num(t.quote_volume ?? t.volume_24h_quote),
        },
      };
    }
    return null;
  },
};

// ------------------------------------------------------------------- MEXC

const MEXC_SPOT_HOST = 'https://api.mexc.com';
const MEXC_FUT_HOST = 'https://contract.mexc.com';

const mexc = {
  key: 'mexc',
  label: 'MEXC',
  tf: {
    futures: { '1m': 'Min1', '5m': 'Min5', '15m': 'Min15', '1h': 'Min60', '4h': 'Hour4', '1d': 'Day1' },
    spot: { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '60m', '4h': '4h', '1d': '1d' },
  },
  native(symbol, market) {
    if (market !== 'futures') return symbol;
    const { base, quote } = splitSymbol(symbol);
    return `${base}_${quote}`;
  },

  async restKlines(market, native, tf, limit, endTime = null) {
    if (market === 'spot') {
      const params = { symbol: native, interval: this.tf.spot[tf], limit };
      if (endTime) {
        params.endTime = endTime;
        // MEXC ignoră `endTime` singur — are nevoie și de marginea de start.
        params.startTime = endTime - (TF_SECONDS[tf] || 3600) * limit * 1000;
      }
      const rows = await getJson(MEXC_SPOT_HOST, '/api/v3/klines', params);
      return rows.map((r) => ({
        time: Math.floor(r[0] / 1000),
        open: num(r[1]), high: num(r[2]), low: num(r[3]), close: num(r[4]),
        volume: num(r[7]),
      }));
    }
    const params = { interval: this.tf.futures[tf] };
    if (endTime) params.end = Math.floor(endTime / 1000);   // MEXC futures: secunde
    const data = await getJson(MEXC_FUT_HOST, `/api/v1/contract/kline/${native}`, params);
    const d = data?.data || {};
    const times = d.time || [];
    const from = Math.max(0, times.length - limit);
    return times.slice(from).map((t, i) => ({
      time: Number(t),
      open: num(d.open[from + i]), high: num(d.high[from + i]),
      low: num(d.low[from + i]), close: num(d.close[from + i]),
      volume: num(d.amount[from + i]),
    }));
  },

  wsUrl: (market) => (market === 'futures'
    ? 'wss://contract.mexc.com/edge' : 'wss://wbs.mexc.com/ws'),

  topic(kind, native, tf, market) {
    if (market === 'futures') {
      if (kind === 'kline') return `kline|${native}|${this.tf.futures[tf]}`;
      if (kind === 'trade') return `deal|${native}`;
      if (kind === 'depth') return `depth|${native}`;
      if (kind === 'ticker') return `ticker|${native}`;
      return null;
    }
    if (kind === 'kline') return `spot@public.kline.v3.api@${native}@${this.tf.spot[tf]}`;
    if (kind === 'trade') return `spot@public.deals.v3.api@${native}`;
    if (kind === 'depth') return `spot@public.limit.depth.v3.api@${native}@20`;
    return null;
  },

  subMessage(topics, id, market) {
    if (market === 'futures') {
      const [channel, symbol, interval] = topics[0].split('|');
      return { method: `sub.${channel}`, param: interval ? { symbol, interval } : { symbol } };
    }
    return { method: 'SUBSCRIPTION', params: topics };
  },
  unsubMessage(topics, id, market) {
    if (market === 'futures') {
      const [channel, symbol] = topics[0].split('|');
      return { method: `unsub.${channel}`, param: { symbol } };
    }
    return { method: 'UNSUBSCRIPTION', params: topics };
  },
  oneAtATime: true,

  parse(raw) {
    // Futures (contract.mexc.com)
    if (raw.channel && raw.data) {
      const ch = raw.channel;
      if (ch === 'push.kline') {
        const k = raw.data;
        return {
          topic: `kline|${k.symbol}|${k.interval}`, kind: 'kline',
          payload: {
            bar: {
              time: Number(k.t), open: num(k.o), high: num(k.h), low: num(k.l), close: num(k.c),
              volume: num(k.a),
            },
            closed: false,
          },
        };
      }
      if (ch === 'push.deal') {
        const t = raw.data;
        return {
          topic: `deal|${raw.symbol}`, kind: 'trade',
          payload: { time: Math.floor(t.t / 1000), price: num(t.p), qty: num(t.v), buyerMaker: t.T === 2 },
        };
      }
      if (ch === 'push.depth') {
        return {
          topic: `depth|${raw.symbol}`, kind: 'depth',
          payload: {
            bids: (raw.data.bids || []).map((x) => [num(x[0]), num(x[1])]),
            asks: (raw.data.asks || []).map((x) => [num(x[0]), num(x[1])]),
          },
        };
      }
      return null;
    }
    // Spot (wbs.mexc.com)
    const ch = raw.c;
    if (!ch || !raw.d) return null;
    if (ch.includes('public.kline')) {
      const k = raw.d.k;
      return {
        topic: ch, kind: 'kline',
        payload: {
          bar: {
            time: Number(k.t), open: num(k.o), high: num(k.h), low: num(k.l), close: num(k.c),
            volume: num(k.a),
          },
          closed: false,
        },
      };
    }
    if (ch.includes('public.deals')) {
      const t = (raw.d.deals || [])[0];
      if (!t) return null;
      return {
        topic: ch, kind: 'trade',
        payload: { time: Math.floor(t.t / 1000), price: num(t.p), qty: num(t.v), buyerMaker: t.S === 2 },
      };
    }
    if (ch.includes('limit.depth')) {
      return {
        topic: ch, kind: 'depth',
        payload: {
          bids: (raw.d.bids || []).map((x) => [num(x.p), num(x.v)]),
          asks: (raw.d.asks || []).map((x) => [num(x.p), num(x.v)]),
        },
      };
    }
    return null;
  },
};

// ---------------------------------------------------------------- registru

export const ADAPTERS = { binance, bybit, okx, bitget, gate, mexc };

export const EXCHANGE_LIST = Object.values(ADAPTERS).map((a) => ({ key: a.key, label: a.label }));

export function adapter(key) {
  return ADAPTERS[key] || binance;
}

/** Simbolul canonic (BTCUSDT) -> simbolul nativ al bursei. */
export function nativeSymbol(exchange, market, symbol) {
  return adapter(exchange).native(symbol, market);
}

// ------------------------------------------------------- stakan adânc (REST)
//
// Pentru plotnostile de pe graficul mare. Stream-urile de stakan din adaptoare dau doar
// 5–50 de niveluri — pe o monedă mică asta înseamnă câteva zecimi de procent, iar o
// plotnoste la 2% nu apare deloc. Prin REST: Binance și Bybit dau ±5%, OKX ±4%,
// Bitget ±1% (măsurat pe DOGE). Toate patru permit citirea directă din browser (CORS).

let okxCt = null;
async function okxContract(instId) {
  if (!okxCt) {
    const data = await getJson(OKX_HOST, '/api/v5/public/instruments', { instType: 'SWAP' });
    okxCt = Object.fromEntries((data?.data || []).map((r) => [r.instId, num(r.ctVal)]));
  }
  return okxCt[instId] || 0;
}

const DEPTH = {
  async binance(sym) {
    const d = await getJson(BINANCE_HOST.futures, '/fapi/v1/depth', { symbol: sym, limit: 500 });
    return { bids: d.bids, asks: d.asks, mult: 1 };
  },
  async bybit(sym) {
    const d = await getJson(BYBIT_HOST, '/v5/market/orderbook', { category: 'linear', symbol: sym, limit: 500 });
    return { bids: d?.result?.b || [], asks: d?.result?.a || [], mult: 1 };
  },
  async okx(sym) {
    const inst = okx.native(sym, 'futures');
    const [d, ct] = await Promise.all([
      getJson(OKX_HOST, '/api/v5/market/books', { instId: inst, sz: 400 }),
      okxContract(inst),
    ]);
    const book = d?.data?.[0] || {};
    // La OKX mărimea e în contracte; fără ctVal n-o putem trece în dolari.
    return { bids: book.bids || [], asks: book.asks || [], mult: ct };
  },
  async bitget(sym) {
    const d = await getJson(BITGET_HOST, '/api/v2/mix/market/merge-depth',
      { symbol: sym, productType: 'USDT-FUTURES', limit: 'max' });
    return { bids: d?.data?.bids || [], asks: d?.data?.asks || [], mult: 1 };
  },
};

/**
 * Stakanul de futures al unei monede, în dolari: `{ bids: [[preț, $]], asks }`.
 * Aruncă eroare dacă bursa nu are moneda — apelantul o ignoră pe bursa aceea.
 */
export async function restDepth(exchange, symbol) {
  const fn = DEPTH[exchange];
  if (!fn) return null;
  const { bids, asks, mult } = await fn(symbol);
  if (!mult) return null;
  const conv = (rows) => rows.map((r) => {
    const p = num(r[0]);
    return [p, p * num(r[1]) * mult];
  });
  return { bids: conv(bids), asks: conv(asks) };
}

/** Timeframe-urile pe care le expunem în UI (identic cu ScalpX). */
export const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h', '1d'];
